import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, X, Upload, Layout, FileText, ArrowUp, ArrowDown, Droplet, Download } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { toast } from "sonner";
import { ContentEditor } from "../components/ContentEditor";

const EMPTY = {
  category: "rural", title: "", description: "", location: "",
  hectares: "", price_label: "Consultar", cover_image: "", images: [], features: [],
};

export default function AdminDashboard() {
  const { admin, checking, logout } = useAuth();
  const { reload: reloadContent } = useContent();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("properties"); // "properties" | "content"

  useEffect(() => { if (!checking && !admin) navigate("/admin/login"); }, [admin, checking, navigate]);

  const load = () => api.get("/properties").then(({ data }) => setItems(data));
  useEffect(() => { if (admin) load(); }, [admin]);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const onNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const onEdit = (p) => { setEditing({ ...p }); setOpen(true); };
  const onDelete = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.title}"?`)) return;
    await api.delete(`/admin/properties/${p.id}`);
    toast.success("Eliminado");
    load();
  };

  const onMove = async (p, direction) => {
    try {
      const { data } = await api.post(`/admin/properties/${p.id}/move`, { direction });
      if (data.moved) {
        toast.success(direction === "up" ? "Movido arriba" : "Movido abajo");
        load();
      }
    } catch {
      toast.error("Error al reordenar");
    }
  };

  const [watermarking, setWatermarking] = useState(false);
  const onRewatermark = async () => {
    if (!window.confirm("¿Aplicar marca de agua a TODAS las fotos subidas? Esto solo se debe hacer UNA VEZ. Si ya tienen watermark, se aplicará otra vez. ¿Continuar?")) return;
    setWatermarking(true);
    try {
      const { data } = await api.post("/admin/properties/rewatermark-all");
      toast.success(`Listo: ${data.images_watermarked} imágenes con marca de agua en ${data.properties_updated} propiedades. ${data.external_urls_skipped} imágenes externas omitidas.`);
      load();
    } catch {
      toast.error("Error al aplicar marca de agua");
    } finally { setWatermarking(false); }
  };

  const onDownload = async (p) => {
    try {
      toast.info("Preparando descarga...");
      const res = await api.get(`/admin/properties/${p.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const slug = (p.title || "propiedad").toLowerCase().replace(/[^\w]+/g, "-").substring(0, 60);
      a.download = `impecland_${slug}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Descarga iniciada");
    } catch (e) {
      toast.error("Error al descargar");
    }
  };

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/properties/${data.id}`, data);
      else await api.post("/admin/properties", data);
      toast.success("Guardado");
      setOpen(false); setEditing(null); load();
    } catch (e) {
      toast.error("Error al guardar");
    }
  };

  if (checking || !admin) return null;

  return (
    <div className="pt-28 pb-32" data-testid="admin-dashboard">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-2">Admin</div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-white">Panel de gestión</h1>
          </div>
          <div className="flex gap-3 flex-wrap">
            {tab === "properties" && (
              <>
                <button onClick={onNew} className="flex items-center gap-2 bg-[#C5A059] text-[#0A0F16] px-5 py-3 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#E5C07B]" data-testid="admin-new-property">
                  <Plus size={14} /> Nueva
                </button>
                <button
                  onClick={onRewatermark}
                  disabled={watermarking}
                  title="Aplicar marca de agua a todas las fotos subidas (solo afecta fotos internas, no URLs externas)"
                  className="flex items-center gap-2 border border-[#C5A059]/40 hover:border-[#C5A059] hover:bg-[#C5A059]/10 text-[#C5A059] px-5 py-3 text-xs tracking-[0.2em] uppercase font-medium disabled:opacity-50"
                  data-testid="admin-rewatermark"
                >
                  <Droplet size={14} /> {watermarking ? "Procesando..." : "Marca de agua"}
                </button>
              </>
            )}
            <button onClick={doLogout} className="flex items-center gap-2 border border-[#C5A059]/30 text-white px-5 py-3 text-xs tracking-[0.2em] uppercase hover:border-[#C5A059]" data-testid="admin-logout">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>

        {/* Main tabs */}
        <div className="flex gap-2 mb-10 border-b border-[#C5A059]/15">
          <button
            onClick={() => setTab("properties")}
            className={`flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold px-5 py-3 border-b-2 transition-all ${
              tab === "properties" ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-[#A3A9B2] hover:text-white"
            }`}
            data-testid="admin-tab-properties"
          >
            <Layout size={14} /> Propiedades
          </button>
          <button
            onClick={() => { setTab("content"); reloadContent(); }}
            className={`flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold px-5 py-3 border-b-2 transition-all ${
              tab === "content" ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-[#A3A9B2] hover:text-white"
            }`}
            data-testid="admin-tab-content"
          >
            <FileText size={14} /> Contenido del sitio
          </button>
        </div>

        {tab === "properties" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((p, idx) => (
              <div key={p.id} className="border border-[#C5A059]/15 bg-[#111822] overflow-hidden relative" data-testid={`admin-card-${p.id}`}>
                {/* Position badge + reorder buttons */}
                <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
                  <span className="bg-[#0A0F16]/90 border border-[#C5A059]/40 text-[#C5A059] text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-1">
                    #{idx + 1}
                  </span>
                </div>
                <div className="absolute top-3 right-3 z-10 flex flex-col gap-1">
                  <button
                    onClick={() => onMove(p, "up")}
                    disabled={idx === 0}
                    className="w-9 h-9 flex items-center justify-center bg-[#0A0F16]/90 border border-[#C5A059]/40 hover:border-[#C5A059] hover:bg-[#C5A059]/15 text-[#C5A059] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Mover arriba"
                    data-testid={`admin-move-up-${p.id}`}
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    onClick={() => onMove(p, "down")}
                    disabled={idx === items.length - 1}
                    className="w-9 h-9 flex items-center justify-center bg-[#0A0F16]/90 border border-[#C5A059]/40 hover:border-[#C5A059] hover:bg-[#C5A059]/15 text-[#C5A059] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Mover abajo"
                    data-testid={`admin-move-down-${p.id}`}
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>

                <div className="aspect-[16/10] bg-[#0A0F16]">
                  {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#C5A059] mb-1">{p.category}</div>
                  <div className="font-serif-display text-xl text-white mb-1">{p.title}</div>
                  <div className="text-xs text-[#A3A9B2] mb-4">{p.location}</div>
                  <div className="flex gap-2">
                    <button onClick={() => onEdit(p)} className="flex-1 border border-[#C5A059]/30 hover:border-[#C5A059] text-xs tracking-[0.15em] uppercase py-2 flex items-center justify-center gap-1 text-white" data-testid={`admin-edit-${p.id}`}>
                      <Pencil size={12}/> Editar
                    </button>
                    <button onClick={() => onDownload(p)} title="Descargar todas las fotos en ZIP" className="border border-[#C5A059]/30 hover:border-[#C5A059] hover:bg-[#C5A059]/10 text-[#C5A059] text-xs uppercase px-3 py-2" data-testid={`admin-download-${p.id}`}>
                      <Download size={12}/>
                    </button>
                    <button onClick={() => onDelete(p)} className="border border-red-500/40 hover:border-red-500 text-red-400 text-xs uppercase px-3 py-2" data-testid={`admin-delete-${p.id}`}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "content" && <ContentEditor />}
      </div>

      {open && editing && <PropertyEditor value={editing} onCancel={() => { setOpen(false); setEditing(null); }} onSave={save} />}
    </div>
  );
}

const PropertyEditor = ({ value, onCancel, onSave }) => {
  const [f, setF] = useState(value);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const uploadImage = async (file, field = "cover_image") => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (field === "cover_image") setF((prev) => ({ ...prev, cover_image: data.url }));
      else setF((prev) => ({ ...prev, images: [...(prev.images || []), data.url] }));
    } catch { toast.error("Error al subir"); }
    finally { setUploading(false); }
  };

  const replaceImage = async (file, index) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => {
        const arr = [...(prev.images || [])];
        arr[index] = data.url;
        return { ...prev, images: arr };
      });
      toast.success("Foto reemplazada con marca de agua");
    } catch { toast.error("Error al reemplazar"); }
    finally { setUploading(false); }
  };

  const replaceCover = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, cover_image: data.url }));
      toast.success("Imagen principal reemplazada");
    } catch { toast.error("Error al reemplazar"); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A0F16]/95 backdrop-blur-md overflow-y-auto" data-testid="property-editor-modal">
      <button onClick={onCancel} className="fixed top-6 right-6 z-[110] w-12 h-12 flex items-center justify-center border border-[#C5A059]/30 hover:border-[#C5A059] text-white"><X/></button>
      <div className="max-w-3xl mx-auto px-6 py-20">
        <h2 className="font-serif-display text-4xl text-white mb-10">{f.id ? "Editar propiedad" : "Nueva propiedad"}</h2>
        <div className="space-y-6">
          <Field label="Categoría">
            <select value={f.category} onChange={upd("category")} className="editorial-input w-full py-3 bg-[#111822]" data-testid="editor-category">
              <option value="rural">Rural</option><option value="urban">Urbano</option><option value="industrial">Industrial</option>
            </select>
          </Field>
          <Field label="Título"><input value={f.title} onChange={upd("title")} className="editorial-input w-full py-3" data-testid="editor-title" /></Field>
          <Field label="Ubicación"><input value={f.location} onChange={upd("location")} className="editorial-input w-full py-3" data-testid="editor-location" /></Field>
          <div className="grid grid-cols-2 gap-6">
            <Field label="Superficie"><input value={f.hectares || ""} onChange={upd("hectares")} className="editorial-input w-full py-3" /></Field>
            <Field label="Valor"><input value={f.price_label || ""} onChange={upd("price_label")} className="editorial-input w-full py-3" /></Field>
          </div>
          <Field label="Descripción"><textarea value={f.description} onChange={upd("description")} rows={5} className="editorial-input w-full py-3 resize-none" data-testid="editor-description" /></Field>

          <Field label="Imagen principal (URL o subir — al subir se aplica marca de agua automática)">
            <div className="flex gap-2 items-start">
              <div className="flex-1 flex gap-2">
                <input value={f.cover_image} onChange={upd("cover_image")} placeholder="https://..." className="editorial-input flex-1 py-3" />
                <label className="border border-[#C5A059]/30 hover:border-[#C5A059] px-4 py-3 text-xs uppercase tracking-[0.15em] text-white cursor-pointer flex items-center gap-2 shrink-0">
                  <Upload size={12} /> Subir
                  <input type="file" ref={fileRef} hidden accept="image/*" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], "cover_image")} />
                </label>
              </div>
            </div>
            {f.cover_image && (
              <div className="mt-3 relative inline-block group">
                <img src={f.cover_image} alt="" className="h-32 object-cover" />
                <label className="absolute inset-0 bg-[#0A0F16]/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                  <span className="bg-[#C5A059] text-[#0A0F16] text-[10px] tracking-[0.18em] uppercase font-bold px-3 py-1 flex items-center gap-1">
                    <Upload size={10} /> Reemplazar
                  </span>
                  <input type="file" hidden accept="image/*" onChange={(e) => e.target.files[0] && replaceCover(e.target.files[0])} />
                </label>
              </div>
            )}
          </Field>

          <Field label="Galería de fotos — Click sobre cada foto para reemplazarla con una nueva (sale con marca de agua)">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {(f.images || []).map((url, i) => (
                <div key={i} className="relative group aspect-[4/3] bg-[#0A0F16] border border-[#C5A059]/15">
                  {url ? (
                    <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[10px] text-[#A3A9B2] uppercase">Sin imagen</div>
                  )}
                  <div className="absolute inset-0 bg-[#0A0F16]/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                    <label className="bg-[#C5A059] text-[#0A0F16] text-[10px] tracking-[0.15em] uppercase font-bold px-3 py-1 flex items-center gap-1 cursor-pointer">
                      <Upload size={10} /> Reemplazar
                      <input type="file" hidden accept="image/*" onChange={(e) => e.target.files[0] && replaceImage(e.target.files[0], i)} />
                    </label>
                    <button
                      type="button"
                      onClick={() => setF({ ...f, images: f.images.filter((_, j) => j !== i) })}
                      className="border border-red-500/60 text-red-400 hover:bg-red-500 hover:text-white text-[10px] tracking-[0.15em] uppercase px-3 py-1"
                    >
                      ✕ Eliminar
                    </button>
                  </div>
                  <div className="absolute top-1 left-1 bg-[#0A0F16]/80 text-[#C5A059] text-[10px] font-bold px-1.5 py-0.5 border border-[#C5A059]/30">
                    #{i + 1}
                  </div>
                </div>
              ))}
              <label className="aspect-[4/3] border-2 border-dashed border-[#C5A059]/40 hover:border-[#C5A059] hover:bg-[#C5A059]/5 transition flex flex-col items-center justify-center gap-2 cursor-pointer text-[#C5A059]">
                <Upload size={20} />
                <span className="text-[10px] tracking-[0.18em] uppercase font-semibold">Subir foto</span>
                <input type="file" hidden accept="image/*" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], "images")} />
              </label>
            </div>
            <div className="mt-3 flex justify-between text-[10px] text-[#A3A9B2]">
              <span>{(f.images || []).length} foto(s) en la galería · Pasá el cursor sobre cada una</span>
              <button
                type="button"
                onClick={() => setF({ ...f, images: [...(f.images || []), ""] })}
                className="text-[#C5A059] hover:underline tracking-[0.15em] uppercase"
              >
                + Agregar URL externa
              </button>
            </div>
          </Field>

          <Field label="Características (una por línea)">
            <textarea
              value={(f.features || []).join("\n")}
              onChange={(e) => setF({ ...f, features: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
              rows={4} className="editorial-input w-full py-3 resize-none"
            />
          </Field>

          {uploading && <div className="text-xs text-[#C5A059]">Subiendo...</div>}

          <div className="flex gap-3 pt-4">
            <button onClick={() => onSave(f)} className="bg-[#C5A059] text-[#0A0F16] px-8 py-3 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#E5C07B]" data-testid="editor-save">Guardar</button>
            <button onClick={onCancel} className="border border-[#C5A059]/30 text-white px-8 py-3 text-xs tracking-[0.2em] uppercase">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-2 block">{label}</label>
    {children}
  </div>
);
